/**
 * TRAC-1089: the proof that this browser owns a chat session.
 *
 * The backend hands out a random session key exactly once, when a session is created
 * (`POST /agent/session-init`, or `/agent/user-message` when that call creates it).
 * Every later call on that session has to carry it in `X-Traci-Session-Key`, or the
 * backend answers 403. The chat token is not enough: an anonymous embed token is
 * re-minted mid-visit with a new identity, so the key is what survives that.
 *
 * Keys are stored per session id, and the last few are kept rather than only the
 * current one: a clear fires its end-handoff for the OLD session after the new one
 * exists, and that call still needs the old session's key.
 *
 * Never send a key anywhere but our own backend. In particular, never put it in a
 * message to the parent page (TRAC-1010) or on a URL.
 */
export const SESSION_KEY_HEADER = 'X-Traci-Session-Key';

const MAX_KEYS = 5;

type Entry = [sessionId: string, key: string];

export interface SessionKeyStore {
  remember(sessionId: string, key: string | null | undefined): void;
  get(sessionId: string | null | undefined): string | null;
  /** `{ 'X-Traci-Session-Key': key }` when we hold one for this session, else `{}`. */
  headers(sessionId: string | null | undefined): Record<string, string>;
  forget(sessionId: string | null | undefined): void;
}

export function createSessionKeyStore(storageKey: string): SessionKeyStore {
  const read = (): Entry[] => {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(parsed)
        ? parsed.filter((e): e is Entry => Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'string')
        : [];
    } catch {
      return [];
    }
  };
  const write = (entries: Entry[]) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(entries.slice(-MAX_KEYS)));
    } catch { /* storage blocked: the key is lost, and the next 403 starts a fresh session */ }
  };
  const get = (sessionId: string | null | undefined): string | null => {
    if (!sessionId) return null;
    const hit = read().find(([sid]) => sid === sessionId);
    return hit ? hit[1] : null;
  };
  return {
    remember(sessionId, key) {
      if (!sessionId || !key) return;
      write([...read().filter(([sid]) => sid !== sessionId), [sessionId, key]]);
    },
    get,
    headers(sessionId) {
      const key = get(sessionId);
      const out: Record<string, string> = {};
      if (key) out[SESSION_KEY_HEADER] = key;
      return out;
    },
    forget(sessionId) {
      if (!sessionId) return;
      write(read().filter(([sid]) => sid !== sessionId));
    },
  };
}

/** The 403 body the backend sends when this browser does not own the session it named. */
export function isSessionNotOwned(status: number, body: unknown): boolean {
  if (status !== 403 || !body || typeof body !== 'object') return false;
  const detail = (body as { detail?: unknown }).detail;
  return !!detail && typeof detail === 'object' && (detail as { code?: unknown }).code === 'session_not_owned';
}

/**
 * The chat's own key store, registered by RealChatAgentService when it loads, so
 * other session-scoped callers (turn feedback) send the same key without their own
 * copy of the storage namespace.
 */
let chatSessionKeys: SessionKeyStore | null = null;

export function registerChatSessionKeys(store: SessionKeyStore): void {
  chatSessionKeys = store;
}

/** `{ 'X-Traci-Session-Key': key }` for a chat session, or `{}` if we hold none. */
export function chatSessionKeyHeaders(sessionId: string | null | undefined): Record<string, string> {
  return chatSessionKeys ? chatSessionKeys.headers(sessionId) : {};
}
